import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  CalendarDays,
  User,
  Settings,
  LogOut,
  Syringe,
} from "lucide-react";
import Navbar from "../../components/public/Navbar";

export function authHeaders() {
  const token = localStorage.getItem("customer_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function readUser() {
  try {
    return JSON.parse(localStorage.getItem("customer_user") || "{}");
  } catch {
    return {};
  }
}

function isPhonePlaceholderEmail(email) {
  return Boolean(email) && String(email).endsWith("@phone.bookmyjab.local");
}

export function formatDisplayName(user = {}) {
  const name = (user.full_name || "").trim();
  if (name) return name;
  const phone = String(user.phone || "").replace(/\D/g, "");
  if (phone.length >= 10) {
    const local = phone.slice(-10);
    return `+91 ${local.slice(0, 5)} ${local.slice(5)}`;
  }
  const email = (user.email || "").trim();
  if (email && !isPhonePlaceholderEmail(email)) return email;
  return "Your account";
}

export function formatDisplayEmail(user = {}) {
  const email = (user.email || "").trim();
  if (!email || isPhonePlaceholderEmail(email)) return null;
  return email;
}

export function requireCustomerAuth(navigate) {
  if (!localStorage.getItem("customer_token")) {
    navigate("/login");
    return false;
  }
  return true;
}

export function notifyAuthChanged() {
  window.dispatchEvent(new Event("customer-auth-changed"));
}

export function persistCustomerSession(data) {
  if (!data?.token) return;
  localStorage.setItem("customer_token", data.token);
  localStorage.setItem("customer_role", data.role || "customer");
  localStorage.setItem("customer_user", JSON.stringify(data.user || {}));
  notifyAuthChanged();
}

export function isCustomerLoggedIn() {
  return Boolean(localStorage.getItem("customer_token"));
}

const SIDEBAR = [
  { id: "overview", to: "/dashboard", label: "Overview", icon: LayoutDashboard },
  { id: "bookings", to: "/dashboard?tab=bookings", label: "My Bookings", icon: CalendarDays },
  { id: "vaccines", to: "/dashboard?tab=vaccines", label: "My vaccines", icon: Syringe },
  { id: "profile", to: "/profile", label: "Profile", icon: User },
  { id: "settings", to: "/dashboard?tab=settings", label: "Settings", icon: Settings },
];

export function CustomerShell({ children, active = "overview" }) {
  const navigate = useNavigate();
  const user = readUser();
  const displayName = formatDisplayName(user);

  const logout = () => {
    localStorage.removeItem("customer_token");
    localStorage.removeItem("customer_user");
    localStorage.removeItem("customer_role");
    notifyAuthChanged();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#f4f5f7] pb-24" data-testid="customer-account-shell">
      <Navbar accountMode />
      <div className="pt-[96px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
            <div>
              <h1
                className="text-3xl md:text-4xl font-bold text-[#673c96] tracking-tight"
                style={{ fontFamily: "Poppins, sans-serif" }}
              >
                {displayName}
              </h1>
              <p className="mt-1 text-[#6b7280]" style={{ fontFamily: "Montserrat, sans-serif" }}>
                Manage your healthcare bookings
              </p>
            </div>
            <button
              type="button"
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm font-medium text-[#6b7280] hover:text-[#311b52] transition-colors"
              style={{ fontFamily: "Montserrat, sans-serif" }}
              data-testid="account-logout"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>

          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            <aside className="lg:w-52 shrink-0">
              <nav className="flex lg:flex-col gap-1 overflow-x-auto pb-1 lg:pb-0" data-testid="account-sidebar">
                {SIDEBAR.map((item) => {
                  const Icon = item.icon;
                  const isActive = active === item.id;
                  return (
                    <Link
                      key={item.id}
                      to={item.to}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
                        isActive
                          ? "bg-[#673c96] text-white shadow-sm"
                          : "text-[#4b5563] hover:bg-white hover:text-[#311b52]"
                      }`}
                      style={{ fontFamily: "Montserrat, sans-serif" }}
                    >
                      <Icon className={`w-5 h-5 ${isActive ? "text-white" : "text-[#673c96]"}`} />
                      {item.label}
                    </Link>
                  );
                })}
              </nav>
            </aside>
            <main className="flex-1 min-w-0">{children}</main>
          </div>
        </div>
      </div>
    </div>
  );
}
